import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Settings,
  Clock,
  Cpu,
  FileText,
  Brain,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
} from "lucide-react";
import type { Generation, Feedback } from "../../types/conversation";

interface GenerationDetailsProps {
  generation: Generation;
  reasoning?: string;
}

export function GenerationDetails({
  generation,
  reasoning,
}: GenerationDetailsProps): JSX.Element {
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const formatLatency = (latencyMs: number): string => {
    if (latencyMs < 1000) {
      return `${latencyMs}ms`;
    }
    return `${(latencyMs / 1000).toFixed(1)}s`;
  };

  const formatScore = (score: number | undefined): string => {
    if (score === undefined) return "N/A";
    return (score * 100).toFixed(1) + "%";
  };

  const renderFeedbackSummary = (feedback: Feedback[]) => {
    const positive = feedback.filter((f) => f.rating === "up").length;
    const negative = feedback.filter((f) => f.rating === "down").length;
    const total = feedback.length;

    if (total === 0) {
      return <span className="text-gray-500">No feedback</span>;
    }

    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <ThumbsUp className="w-3 h-3 text-green-500" />
          <span className="text-green-600 font-medium">{positive}</span>
        </div>
        <div className="flex items-center gap-1">
          <ThumbsDown className="w-3 h-3 text-red-500" />
          <span className="text-red-600 font-medium">{negative}</span>
        </div>
        <span className="text-gray-500">
          ({((positive / total) * 100).toFixed(0)}% positive)
        </span>
      </div>
    );
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <Settings className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-medium text-gray-700">
          Generation Details
        </h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        {/* Model Information */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Cpu className="w-3 h-3 text-blue-500" />
            <span className="font-medium text-gray-700">Model</span>
          </div>
          <div className="pl-5">
            <p className="text-gray-900 font-medium">
              {generation.model_display_name}
            </p>
            <p className="text-gray-500 text-xs">{generation.model}</p>
            {generation.temperature !== undefined && (
              <div className="flex gap-4 mt-1">
                <span className="text-gray-600">
                  Temp: {generation.temperature}
                </span>
                {generation.top_p !== undefined && (
                  <span className="text-gray-600">
                    Top-p: {generation.top_p}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Performance */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-green-500" />
            <span className="font-medium text-gray-700">Performance</span>
          </div>
          <div className="pl-5">
            <p className="text-gray-900 font-medium">
              {formatLatency(generation.latency_ms)}
            </p>
            <p className="text-gray-500">Response time</p>
          </div>
        </div>

        {/* Feedback */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-3 h-3 text-blue-500" />
            <span className="font-medium text-gray-700">User Feedback</span>
          </div>
          <div className="pl-5">
            {renderFeedbackSummary(generation.feedback || [])}
          </div>
        </div>

        {/* Documents Used */}
        {generation.documents_used.length > 0 && (
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="w-3 h-3 text-purple-500" />
              <span className="font-medium text-gray-700">
                Documents Used ({generation.documents_used.length})
              </span>
            </div>
            <div className="pl-5 space-y-1">
              {generation.documents_used.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex justify-between items-center py-1 px-2 bg-white rounded border"
                >
                  <span className="text-gray-700 truncate" title={doc.title}>
                    {doc.title || "Untitled Document"}
                  </span>
                  {doc.score !== undefined && (
                    <span className="text-gray-500 ml-2 whitespace-nowrap">
                      {formatScore(doc.score)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retrieval Metadata */}
        {generation.context?.retrieval_metadata && (
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center gap-2">
              <Brain className="w-3 h-3 text-orange-500" />
              <span className="font-medium text-gray-700">
                Retrieval Settings
              </span>
            </div>
            <div className="pl-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {generation.context.retrieval_metadata.chunks_retrieved && (
                <div>
                  <span className="text-gray-500">Chunks:</span>
                  <span className="ml-1 text-gray-700">
                    {generation.context.retrieval_metadata.chunks_retrieved}
                  </span>
                </div>
              )}
              {generation.context.retrieval_metadata.reranking_used !==
                undefined && (
                <div>
                  <span className="text-gray-500">Reranking:</span>
                  <span className="ml-1 text-gray-700">
                    {generation.context.retrieval_metadata.reranking_used
                      ? "Yes"
                      : "No"}
                  </span>
                </div>
              )}
              {generation.context.retrieval_metadata
                .context_limit_requested && (
                <div>
                  <span className="text-gray-500">Context Limit:</span>
                  <span className="ml-1 text-gray-700">
                    {
                      generation.context.retrieval_metadata
                        .context_limit_requested
                    }
                  </span>
                </div>
              )}
              {generation.context.retrieval_metadata.max_chunks_requested && (
                <div>
                  <span className="text-gray-500">Max Chunks:</span>
                  <span className="ml-1 text-gray-700">
                    {generation.context.retrieval_metadata.max_chunks_requested}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Prompt (Collapsible) */}
        <div className="md:col-span-2 space-y-2">
          <button
            onClick={() => setShowSystemPrompt(!showSystemPrompt)}
            className="flex items-center gap-2 w-full text-left hover:text-gray-900"
          >
            {showSystemPrompt ? (
              <ChevronDown className="w-3 h-3 text-gray-500" />
            ) : (
              <ChevronRight className="w-3 h-3 text-gray-500" />
            )}
            <span className="font-medium text-gray-700">System Prompt</span>
          </button>
          {showSystemPrompt && (
            <div className="pl-5">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border max-h-40 overflow-y-auto">
                {generation.system_prompt || "No system prompt available"}
              </pre>
            </div>
          )}
        </div>

        {/* Full Context (Collapsible) */}
        <div className="md:col-span-2 space-y-2">
          <button
            onClick={() => setShowContext(!showContext)}
            className="flex items-center gap-2 w-full text-left hover:text-gray-900"
          >
            {showContext ? (
              <ChevronDown className="w-3 h-3 text-gray-500" />
            ) : (
              <ChevronRight className="w-3 h-3 text-gray-500" />
            )}
            <span className="font-medium text-gray-700">Full Context</span>
          </button>
          {showContext && (
            <div className="pl-5">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border max-h-40 overflow-y-auto">
                {JSON.stringify(generation.context, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Reasoning (Collapsible) */}
        {reasoning && (
          <div className="md:col-span-2 space-y-2">
            <button
              onClick={() => setShowReasoning(!showReasoning)}
              className="flex items-center gap-2 w-full text-left hover:text-gray-900"
            >
              {showReasoning ? (
                <ChevronDown className="w-3 h-3 text-gray-500" />
              ) : (
                <ChevronRight className="w-3 h-3 text-gray-500" />
              )}
              <Brain className="w-3 h-3 text-purple-500" />
              <span className="font-medium text-gray-700">Reasoning</span>
            </button>
            {showReasoning && (
              <div className="pl-5">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border max-h-40 overflow-y-auto">
                  {reasoning}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Detailed Feedback (Collapsible) */}
        {generation.feedback && generation.feedback.length > 0 && (
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-3 h-3 text-blue-500" />
              <span className="font-medium text-gray-700">
                Detailed Feedback ({generation.feedback.length})
              </span>
            </div>
            <div className="pl-5 space-y-2">
              {generation.feedback.map((feedback, index) => (
                <div
                  key={feedback.id || index}
                  className="flex items-start gap-3 py-2 px-3 bg-white rounded border"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {feedback.rating === "up" ? (
                      <ThumbsUp className="w-4 h-4 text-green-500" />
                    ) : (
                      <ThumbsDown className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`font-medium text-sm ${
                          feedback.rating === "up"
                            ? "text-green-700"
                            : "text-red-700"
                        }`}
                      >
                        {feedback.rating === "up" ? "Positive" : "Negative"}{" "}
                        Feedback
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(feedback.created_at).toLocaleString()}
                      </span>
                    </div>
                    {feedback.reason && (
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Reason:</span>{" "}
                        {feedback.reason}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
